// ============================================================================
// Tiers tab — PURE SURFACE (same shape as the Insurance tab: the shell owns the
// client and the state, this file takes data and callbacks and imports nothing
// that talks to the database, so its copy can be rendered and asserted).
//
// ⛔ THE THRESHOLDS ARE NOT HERE. Tier is read off v_subcontractor_tier. Tier 4
// is under 90 days with TCS, Tier 3 is 90+ days with fewer than three active
// facilities, Tier 2 is 90+ days with three or more, Tier 1 is conferred by hand
// (Ben, 2026-09-03) — the view is where that arithmetic lives and the one place
// it may change.
//
// ⛔ The Tier 1 group renders even when it is empty. Zero subs are Tier 1 today;
// hiding the group would read as "there is no such thing".
//
// ⛔ Promote is offered on Tier 2 rows only and demote on Tier 1 rows. That is a
// PRESENTATION rule, not a refusal — Tier 1 has no eligibility floor and no
// revenue gate, and the database door deliberately does not check the tier.
// ============================================================================
import { ArrowDownRight, Award } from 'lucide-react';
import { subChangeReasonShort } from '../sub-replacement/sub-change-reason-names';

export interface TierRow {
  provider_id: string;
  display_name: string;
  tier: 1 | 2 | 3 | 4;
  active_facility_count: number;
  days_with_tcs: number | null;
  tenure_is_estimate: boolean;
  facilities_lost_60d: number;
  loss_events_60d: number;
  bulk_handover_60d: boolean;
  last_loss_on: string | null;
  /** Why — sub-change reason codes behind the losses (#667). */
  loss_reason_codes: string[];
  lead_am_eid: string | null;
  lead_am_name: string | null;
  lead_market: string | null;
  capabilities: string[];
  tier_1: boolean;
}

export const TIER_ORDER: Array<1 | 2 | 3 | 4> = [1, 2, 3, 4];

/** Short forms for the summary row; the long ones stay on the group headings so
 *  the rule a tier encodes is never more than one screen from the number. */
export const TIER_SHORT: Record<1 | 2 | 3 | 4, string> = {
  1: 'Tier 1',
  2: 'Tier 2',
  3: 'Tier 3',
  4: 'Tier 4',
};

export const TIER_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: 'Tier 1 — conferred by hand',
  2: 'Tier 2 — 90+ days, three or more buildings',
  3: 'Tier 3 — 90+ days, under three buildings',
  4: 'Tier 4 — under 90 days with TCS',
};

/** No market on file and no account manager are real answers, so they are
 *  selectable rather than a hole the row falls through. */
export const NO_VALUE = '__none__';

/**
 * ⛔ AN ESTIMATED TENURE IS A FLOOR, AND IT IS RENDERED AS ONE.
 *
 * Ben, 2026-09-12: *"The 143 days is artificial. It is because that is about how
 * many days ago we stood up the database and app, so that is as far back as we
 * have clean data."* Thirty-four of the thirty-eight estimated dates sit on two
 * days in April 2026 — the day the records begin, not the day those subs started.
 * Several of them have been with TCS for years.
 *
 * So an estimate reads **152+ days**, meaning "here before our records start",
 * never "152 days", which would say they are newer than they are. The screen
 * says why once, underneath, rather than leaving a bare plus sign to guess at.
 *
 * ⛔ This changes no tier. Every floored date is already well past ninety days,
 * so nothing is being kept out of, or pushed into, Tier 4 by it — which is the
 * only decision tenure drives.
 */
export function tenureText(row: TierRow): string {
  if (row.days_with_tcs === null) return 'No start date on file';
  if (row.tenure_is_estimate) return `${row.days_with_tcs}+ days`;
  return `${row.days_with_tcs} days`;
}

/** Said once per screen, under any table that shows a tenure. */
export const TENURE_FOOTNOTE =
  'A + means the records only go back that far. The database was stood up in April 2026, so a subcontractor who was already working then shows the platform’s age, not their own.';

export function marketOptions(rows: TierRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.lead_market).filter((v): v is string => !!v))).sort();
}

export function amOptions(rows: TierRow[]): Array<{ eid: string; label: string }> {
  const m = new Map<string, string>();
  for (const r of rows) {
    if (r.lead_am_eid) m.set(r.lead_am_eid, r.lead_am_name ?? r.lead_am_eid);
  }
  return Array.from(m, ([eid, label]) => ({ eid, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function applyTierFilters(
  rows: TierRow[],
  market: string,
  am: string,
): TierRow[] {
  return rows.filter((r) => {
    if (market === NO_VALUE ? r.lead_market !== null : market && r.lead_market !== market)
      return false;
    if (am === NO_VALUE ? r.lead_am_eid !== null : am && r.lead_am_eid !== am) return false;
    return true;
  });
}

/** Subs under 90 days with TCS — including those with no start date on file,
 *  which must not silently read as long-tenured. */
export function underNinetyDays(rows: TierRow[]): TierRow[] {
  return rows.filter((r) => r.days_with_tcs === null || r.days_with_tcs < 90);
}

/** Subs who have lost a building in the last 60 days, worst first. */
export function lostRecently(rows: TierRow[]): TierRow[] {
  return rows
    .filter((r) => r.facilities_lost_60d > 0)
    .sort((a, b) => b.facilities_lost_60d - a.facilities_lost_60d);
}

/**
 * How a loss should READ. ⛔ The number of buildings is not the whole story:
 * one incoming sub taking a whole book on one day is a handover, and a sub
 * losing one building a week is the thing this list exists to catch. Saying
 * which is which is the difference between a list people read and a list people
 * learn to scroll past.
 */
export function lossPhrase(r: TierRow): string {
  const b = r.facilities_lost_60d;
  const buildings = `${b} ended assignment${b === 1 ? '' : 's'}`;
  if (r.bulk_handover_60d && r.loss_events_60d === 1) return `${buildings}, all in one handover`;
  if (r.bulk_handover_60d) return `${buildings} across ${r.loss_events_60d} changes, one of them a whole book at once`;
  if (r.loss_events_60d > 1) return `${buildings} across ${r.loss_events_60d} separate changes`;
  return buildings;
}

/**
 * WHY the buildings were lost, from the reason on each change (#667). Ben's
 * point: "lost 15 buildings" told a manager they were losing people we had
 * removed ourselves. The reason says which.
 */
export function lossWhy(r: TierRow): string {
  const codes = r.loss_reason_codes ?? [];
  return codes.length ? codes.map((c) => subChangeReasonShort(c)).join(' / ') : 'Reason not recorded';
}

/** Every loss is one we cannot explain — shown as a warning, not a judgement. */
export function lossUnexplained(r: TierRow): boolean {
  const codes = r.loss_reason_codes ?? [];
  return codes.length === 0 || codes.every((c) => c === 'sub_reason_not_recorded');
}

/** A sub is at risk when they are new, or losing work, or both. */
export function atRisk(rows: TierRow[]): TierRow[] {
  const ids = new Set<string>();
  const out: TierRow[] = [];
  for (const r of [...lostRecently(rows), ...underNinetyDays(rows)]) {
    if (ids.has(r.provider_id)) continue;
    ids.add(r.provider_id);
    out.push(r);
  }
  return out;
}

export function tierCounts(rows: TierRow[]): Record<1 | 2 | 3 | 4, number> {
  return {
    1: rows.filter((r) => r.tier === 1).length,
    2: rows.filter((r) => r.tier === 2).length,
    3: rows.filter((r) => r.tier === 3).length,
    4: rows.filter((r) => r.tier === 4).length,
  };
}

export function TierRosterView({
  rows,
  allRows,
  market,
  am,
  onMarket,
  onAm,
  busyProviderId,
  onSetTier1,
  renderName,
}: {
  /** Rows after the filters — what the roster renders. */
  rows: TierRow[];
  /** Every row, so the filter lists do not shrink as you filter. */
  allRows: TierRow[];
  market: string;
  am: string;
  onMarket: (v: string) => void;
  onAm: (v: string) => void;
  busyProviderId: string | null;
  onSetTier1: (row: TierRow, next: boolean) => void;
  renderName: (row: TierRow) => React.ReactNode;
}) {
  const counts = tierCounts(rows);
  const newSubs = underNinetyDays(rows);
  const losing = lostRecently(rows);

  return (
    <div className="space-y-4">
      {/* ---- Summary. The counts a person wants before they read a single row. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {TIER_ORDER.map((t) => (
          <Tile key={t} label={TIER_SHORT[t]} value={counts[t]} />
        ))}
        <Tile label="Under 90 days" value={newSubs.length} tone="warn" />
        <Tile
          label="Ended assignments · 60 days"
          value={losing.reduce((a, r) => a + r.facilities_lost_60d, 0)}
          tone={losing.length ? 'risk' : undefined}
          foot={`${losing.length} sub${losing.length === 1 ? '' : 's'}`}
        />
      </div>

      {/* ---- Filters ------------------------------------------------------ */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select
          value={market}
          onChange={(e) => onMarket(e.target.value)}
          className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5"
          aria-label="Filter by market"
        >
          <option value="">All markets</option>
          {marketOptions(allRows).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
          <option value={NO_VALUE}>No market on file</option>
        </select>
        <select
          value={am}
          onChange={(e) => onAm(e.target.value)}
          className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5"
          aria-label="Filter by account manager"
        >
          <option value="">All account managers</option>
          {amOptions(allRows).map((a) => (
            <option key={a.eid} value={a.eid}>
              {a.label}
            </option>
          ))}
          <option value={NO_VALUE}>No account manager</option>
        </select>
        <span className="text-xs text-tcs-text-muted">
          {rows.length} of {allRows.length} active field subs
        </span>
      </div>

      {/* ---- The roster --------------------------------------------------- */}
      {TIER_ORDER.map((t) => {
        const group = rows.filter((r) => r.tier === t);
        return (
          <section key={t}>
            <h3 className="flex items-center gap-2 border-b border-tcs-border pb-1 text-sm font-semibold text-tcs-text">
              {t === 1 && <Award className="h-4 w-4 text-tcs-text-secondary" />}
              {TIER_LABEL[t]}
              <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-xs font-medium text-tcs-text-secondary">
                {group.length}
              </span>
            </h3>
            {group.length === 0 ? (
              <p className="py-2 text-xs text-tcs-text-muted">
                {t === 1
                  ? 'Nobody has been made Tier 1 yet. Promote from Tier 2 below.'
                  : 'Nobody in this filter.'}
              </p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[46rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-tcs-text-muted">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Subcontractor</th>
                      <th className="py-1 pr-3 font-medium">Buildings</th>
                      <th className="py-1 pr-3 font-medium">With TCS</th>
                      <th className="py-1 pr-3 font-medium">Account manager</th>
                      <th className="py-1 pr-3 font-medium">Market</th>
                      <th className="py-1 pr-3 font-medium">Capabilities</th>
                      <th className="py-1 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((r) => (
                      <tr
                        key={r.provider_id}
                        id={`tier-row-${r.provider_id}`}
                        className="border-t border-tcs-border align-top"
                      >
                        <td className="py-1.5 pr-3 font-medium text-tcs-text">{renderName(r)}</td>
                        <td className="py-1.5 pr-3 tabular-nums">{r.active_facility_count}</td>
                        <td className="py-1.5 pr-3 text-tcs-text-secondary">{tenureText(r)}</td>
                        <td className="py-1.5 pr-3 text-tcs-text-secondary">
                          {r.lead_am_name ?? r.lead_am_eid ?? '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-tcs-text-secondary">
                          {r.lead_market ?? '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-tcs-text-secondary">
                          {r.capabilities.length ? r.capabilities.join(' · ') : '—'}
                        </td>
                        <td className="py-1.5 text-right">
                          {r.tier === 2 && (
                            <button
                              type="button"
                              disabled={busyProviderId === r.provider_id}
                              onClick={() => onSetTier1(r, true)}
                              className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
                            >
                              <Award className="h-3 w-3" /> Make Tier 1
                            </button>
                          )}
                          {r.tier === 1 && (
                            <button
                              type="button"
                              disabled={busyProviderId === r.provider_id}
                              onClick={() => onSetTier1(r, false)}
                              className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
                            >
                              <ArrowDownRight className="h-3 w-3" /> Remove Tier 1
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}

      <p className="text-xs text-tcs-text-muted">{TENURE_FOOTNOTE}</p>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
  foot,
}: {
  label: string;
  value: number | string;
  tone?: 'warn' | 'risk';
  foot?: string;
}) {
  const ink =
    tone === 'risk' ? 'text-st-danger-fg' : tone === 'warn' ? 'text-st-warn-fg' : 'text-tcs-text';
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2">
      <div className={`text-2xl font-bold tabular-nums ${ink}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">{label}</div>
      {foot && <div className="text-[11px] text-tcs-text-muted">{foot}</div>}
    </div>
  );
}

/**
 * At risk — the subs to look at today, on their own screen rather than as a
 * strip above a 65-row roster. Two reasons to be here and a row says which:
 * new with TCS, losing work, or both.
 *
 * ⛔ A loss is described, never just counted. One incoming sub taking a whole
 * book on one day is a handover; one building a week is the thing this list
 * exists to catch, and a number alone cannot tell them apart.
 */
export function AtRiskView({
  rows,
  renderName,
}: {
  rows: TierRow[];
  renderName: (row: TierRow) => React.ReactNode;
}) {
  const list = atRisk(rows);
  const newSubs = underNinetyDays(rows);
  const losing = lostRecently(rows);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="At risk" value={list.length} tone={list.length ? 'risk' : undefined} />
        <Tile label="Under 90 days" value={newSubs.length} tone="warn" />
        <Tile label="Losing work" value={losing.length} tone={losing.length ? 'risk' : undefined} />
        <Tile
          label="Ended assignments · 60 days"
          value={losing.reduce((a, r) => a + r.facilities_lost_60d, 0)}
        />
      </div>

      {list.length === 0 ? (
        <p className="rounded-lg border border-tcs-border bg-tcs-raised p-4 text-sm text-tcs-text-secondary">
          Nobody is inside 90 days and nobody has had an assignment end in the last 60. That is the
          answer, not an empty screen.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-tcs-text-muted">
              <tr>
                <th className="py-1 pr-3 font-medium">Subcontractor</th>
                <th className="py-1 pr-3 font-medium">Why</th>
                <th className="py-1 pr-3 font-medium">Buildings now</th>
                <th className="py-1 pr-3 font-medium">With TCS</th>
                <th className="py-1 pr-3 font-medium">Account manager</th>
                <th className="py-1 pr-3 font-medium">Market</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const isNew = r.days_with_tcs === null || r.days_with_tcs < 90;
                const isLosing = r.facilities_lost_60d > 0;
                return (
                  <tr key={r.provider_id} className="border-t border-tcs-border align-top">
                    <td className="py-1.5 pr-3 font-medium text-tcs-text">
                      {renderName(r)}
                      <span className="ml-1.5 text-xs text-tcs-text-muted">
                        {TIER_SHORT[r.tier]}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {isNew && (
                          <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] font-medium text-st-warn-fg">
                            New sub
                          </span>
                        )}
                        {isLosing && (
                          <span
                            className={`rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] font-medium ${
                              lossUnexplained(r) ? 'text-st-warn-fg' : 'text-st-danger-fg'
                            }`}
                          >
                            {lossWhy(r)} · {lossPhrase(r)}
                          </span>
                        )}
                        {r.active_facility_count === 0 && (
                          <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] font-medium text-st-danger-fg">
                            No building at all
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">{r.active_facility_count}</td>
                    <td className="py-1.5 pr-3 text-tcs-text-secondary">{tenureText(r)}</td>
                    <td className="py-1.5 pr-3 text-tcs-text-secondary">
                      {r.lead_am_name ?? r.lead_am_eid ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-tcs-text-secondary">{r.lead_market ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-tcs-text-muted">{TENURE_FOOTNOTE}</p>
    </div>
  );
}
